"use client";

import { useEffect, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useTranslations } from "next-intl";
import { useRouter, useSearchParams } from "next/navigation";
import { Loader2 } from "lucide-react";
import { Button } from "@mozaic/ui/components/button";
import { completeSso } from "./api";

// The authorisation code and state are single-use, and React Strict Mode runs effects twice in development:
// share one in-flight exchange per state value so the second run cannot consume (and fail) the code.
const exchanges = new Map<string, Promise<unknown>>();

export function SsoCallback() {
  const t = useTranslations("auth");
  const router = useRouter();
  const cache = useQueryClient();
  const params = useSearchParams();
  const state = params.get("state");
  const code = params.get("code");
  const idpError = params.get("error");
  const [failed, setFailed] = useState(false);
  const heading = useRef<HTMLHeadingElement>(null);
  const missing = !state || !code || Boolean(idpError);

  useEffect(() => {
    if (missing) return;
    let current = true;
    const key = state;
    const exchange = exchanges.get(key) ?? completeSso({ state, code });
    exchanges.set(key, exchange);
    exchange.then(
      () => { if (current) { cache.clear(); router.replace("/"); router.refresh(); } },
      () => current && setFailed(true),
    );
    return () => {
      current = false;
    };
  }, [missing, state, code, router, cache]);

  const failure = missing || failed;
  useEffect(() => {
    if (failure) heading.current?.focus();
  }, [failure]);

  if (failure) {
    return (
      <div role="alert" className="space-y-4">
        <h1 ref={heading} tabIndex={-1} className="text-xl font-semibold tracking-tight outline-none">
          {t("sso.failedTitle")}
        </h1>
        <p className="text-sm text-muted-foreground">{t("sso.failedDescription")}</p>
        <Button asChild>
          <a href="/login">{t("sso.backToSignIn")}</a>
        </Button>
      </div>
    );
  }
  return (
    <div role="status" className="flex items-center gap-3">
      <Loader2 className="size-5 animate-spin" aria-hidden="true" />
      <h1 className="text-xl font-semibold tracking-tight">{t("sso.completing")}</h1>
    </div>
  );
}
